/**
 * Switch a CDN platform to BYO (bring-your-own) mode: route the domain through a
 * customer-supplied edge domain instead of the Mlytics-managed edge. Submits
 * `PATCH /waf/platforms/domain/:platform/byo` with the edge domain.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useSetPlatformBYO, wafExtractError, type DomainPlatform } from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';

export function PlatformByoDialog({
  serviceId,
  domain,
  platform,
  open,
  onOpenChange,
}: {
  serviceId: string;
  domain: string;
  platform: DomainPlatform;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { show } = useToast();
  const [edge, setEdge] = useState(platform.edge_domain ?? '');

  useEffect(() => {
    if (open) setEdge(platform.edge_domain ?? '');
  }, [open, platform.edge_domain]);

  const byo = useSetPlatformBYO(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Switched to BYO mode' }); trackFeatureAction('waf', 'cdn', 'edit'); onOpenChange(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  const value = edge.trim();
  const valid = value.length > 0;
  const pending = byo.isPending;

  const submit = () => {
    if (!valid) return;
    byo.mutate({ domain, platform: platform.name, edge_domain: value });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{platform.type === 'byo' ? 'Edit CNAME' : 'Set BYO CNAME'}</DialogTitle>
          <DialogDescription>
            Route <strong>{platform.display_name || platform.name}</strong> for <strong>{domain}</strong> through your own CNAME (BYO) instead of the Mlytics-managed edge.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Input
            placeholder="edge.example.com"
            value={edge}
            onChange={(e) => setEdge(e.target.value)}
            disabled={pending}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            style={{ fontFamily: 'var(--vn-font-mono)' }}
          />
        </DialogBody>
        <DialogFooter>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !valid}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PlatformByoDialog;
